import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';

import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';
import { getStudentDisplayName } from 'approot/students/logic/get-student-display-name';
import { Student, StudentProfile } from '../classroom.constants';
import { isRichtextValueEmpty } from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import { getRichtextAnswer } from 'approot/activity-builder/components/shared/logic/get-richtext-answer';
import type { Pluck } from 'lib/gql/pluck';

export type StudentResponse = StudentProfile & {
  answers: AnswerFragment[];
};

export function getStudentTableAnswers({
  studentsInClass,
  studentAnswers,
}: {
  studentsInClass: Student[];
  studentAnswers: AnswerFragment[];
}): StudentResponse[] {
  const groupedAnswers = groupBy(studentAnswers, 'studentId');

  const studentTableAnswers = studentsInClass.map(student => {
    return {
      studentId: student.id,
      studentName: getStudentDisplayName(student),
      studentEmoji: student.emeji,
      answers: groupedAnswers[student.id] || [],
    };
  });

  const sortedStudentTableAnswers = sortBy(studentTableAnswers, answer =>
    answer.studentName.toLowerCase()
  );

  const studentsWithAnswers = sortedStudentTableAnswers.filter(student =>
    hasStudentTableAnswer(student)
  );

  const studentsWithoutAnswers = sortedStudentTableAnswers.filter(
    student => !hasStudentTableAnswer(student)
  );

  return [...studentsWithAnswers, ...studentsWithoutAnswers];
}

export function hasStudentTableAnswer(response: StudentResponse): boolean {
  return response.answers.some(
    a =>
      !isRichtextValueEmpty(
        (
          a.answer as Extract<
            Pluck<AnswerFragment, 'answer'>,
            { __typename: 'RichtextAnswer' }
          >
        ).richtext
      )
  );
}

export function getTableStudentAnswerCount(answers: AnswerFragment[]): number {
  const studentIds = new Set<number>();

  answers.forEach(answer => {
    if (answer.studentId && !isRichtextValueEmpty(getRichtextAnswer(answer))) {
      studentIds.add(answer.studentId);
    }
  });

  return studentIds.size;
}
